// The Design workspace (DRAW_PLAN §10). D0: control bar, tool bar, tabbed docks, status line, canvas with
// rulers and artboards, and the start screen (new document from a size preset, or one of your designs).
import { Megaphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLang, useT, type MessageKey } from '../../i18n';
import { createDesign, listDesigns, openDesign, saveDesignNow, useDesign, lastDesignName } from '../state';
import { ARTBOARD_PRESETS } from '../model/defaults';
import { DesignCanvas, fitToWindow, zoomTo } from './DesignCanvas';
import { renameDoc } from '../commands/commands';
import { useDesignKeyboard } from './useDesignKeyboard';
import { DOCKS, TOOLS, useDesignUi, type DockId } from './designUi';
import { FillStrokeBoxes } from './FillStrokeBoxes';
import s from './Design.module.css';
import { useSimple } from '../../state/simple';
import { useAdUi } from '../ad/actions';
import { AdStudio } from './ad/AdStudio';
import { TemplatePicker } from './ad/TemplatePicker';
import { BrandDialog } from './ad/BrandDialog';
import { loadFonts } from '../text/fonts';
import { onBitmap } from '../assets';
import { renderer } from '../render/instance';

/** Fonts (M14) load once; when they arrive, and whenever a placed image finishes decoding, redraw. */
function useFontsAndImages() {
  useEffect(() => {
    const bump = () => useDesign.setState((st) => ({ version: st.version + 1 }));
    void loadFonts().then(() => {
      renderer.rebuild();
      bump();
    });
    return onBitmap(() => {
      renderer.invalidateAll();
      bump();
    });
  }, []);
}

export function DesignWorkspace() {
  const doc = useDesign((st) => st.doc);
  const [checked, setChecked] = useState(false);
  const mode = useSimple((st) => st.mode);
  const picker = useAdUi((a) => a.picker);
  useDesignKeyboard();
  useFontsAndImages();
  // Reopen the last document once.
  useEffect(() => {
    if (doc || checked) return;
    const last = lastDesignName();
    void (last ? openDesign(last) : Promise.resolve(false)).finally(() => setChecked(true));
  }, [doc, checked]);
  // M14: Simple mode is the ad studio (template picker, or the ad and its form).
  if (mode === 'simple')
    return (
      <>
        {checked ? <AdStudio /> : null}
        <BrandDialog />
      </>
    );
  return (
    <>
      {picker ? <TemplatePicker /> : !doc ? checked ? <StartScreen /> : null : <Workspace />}
      <BrandDialog />
    </>
  );
}

function Workspace() {
  const t = useT();
  const doc = useDesign((st) => st.doc)!;
  const save = useDesign((st) => st.save);
  const saveError = useDesign((st) => st.saveError);
  const canUndo = useDesign((st) => st.history.past.length > 0);
  const canRedo = useDesign((st) => st.history.future.length > 0);
  const zoom = useDesign((st) => st.view.zoom);
  const tool = useDesignUi((st) => st.tool);
  const dock = useDesignUi((st) => st.dock);
  const [name, setName] = useState(doc.name);
  useEffect(() => setName(doc.name), [doc.name]);
  return (
    <div className={s.ws} data-testid="design-workspace">
      <div className={s.ctrl}>
        <input
          type="text"
          aria-label={t('designDocName')}
          data-testid="design-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name.trim() && name !== doc.name && useDesign.getState().run(renameDoc(name.trim()))}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
        <button onClick={() => useDesign.getState().close()} data-testid="design-new">
          {t('designNewOrOpen')}
        </button>
        <button onClick={() => useAdUi.setState({ picker: true })} data-testid="design-templates">
          {t('adTemplates')}
        </button>
        <button disabled={!canUndo} onClick={() => useDesign.getState().undo()} title="Ctrl+Z">
          {t('undo')}
        </button>
        <button disabled={!canRedo} onClick={() => useDesign.getState().redo()} title="Ctrl+Shift+Z">
          {t('redo')}
        </button>
        <span style={{ flex: 1 }} />
        <span data-testid="design-save-state" title={saveError ?? undefined}>
          {t(
            save === 'saved'
              ? 'designSaved'
              : save === 'saving'
                ? 'designSaving'
                : save === 'error'
                  ? 'designSaveFailed'
                  : 'designUnsaved',
          )}
        </span>
        <button onClick={() => void saveDesignNow()} title="Ctrl+S">
          {t('designSave')}
        </button>
      </div>
      <nav className={s.tools} aria-label={t('designTools')}>
        {TOOLS.map((tl) => (
          <button
            key={tl.id}
            className={`${s.tool} ${tool === tl.id ? s.toolOn : ''}`}
            title={`${t(tl.label)} (${tl.key})`}
            aria-label={t(tl.label)}
            aria-pressed={tool === tl.id}
            data-testid={`tool-${tl.id}`}
            disabled={!tl.ready}
            onClick={() => useDesignUi.getState().set({ tool: tl.id })}
          >
            {tl.icon}
          </button>
        ))}
        <FillStrokeBoxes />
      </nav>
      <DesignCanvas />
      <aside className={s.dock}>
        <div className={s.tabs} role="tablist">
          {DOCKS.map((d) => (
            <button
              key={d.id}
              role="tab"
              aria-selected={dock === d.id}
              className={dock === d.id ? s.tabOn : ''}
              data-testid={`dock-${d.id}`}
              onClick={() => useDesignUi.getState().set({ dock: d.id })}
            >
              {t(d.label)}
            </button>
          ))}
        </div>
        <div className={s.dockBody}>
          <DockBody id={dock} />
        </div>
      </aside>
      <div className={s.status}>
        <button onClick={fitToWindow} title="Ctrl+0">
          {t('zoomFit')}
        </button>
        <button onClick={() => zoomTo(1)} title="Ctrl+1">
          100%
        </button>
        <span data-testid="design-zoom">{Math.round(zoom * 100)}%</span>
        <span>{doc.artboards.map((a) => a.name).join(' · ')}</span>
        <ViewToggles />
        <span style={{ flex: 1 }} />
        <span>{t(TOOLS.find((x) => x.id === tool)?.hint ?? 'designHintNone')}</span>
      </div>
    </div>
  );
}

/** View toggles (DRAW_PLAN §5): snapping on/off, grid / pixel snapping, smart guides, grid and guide display. */
function ViewToggles() {
  const t = useT();
  const snap = useDesignUi((st) => st.snap);
  const showGrid = useDesignUi((st) => st.showGrid);
  const showGuides = useDesignUi((st) => st.showGuides);
  const setSnap = (k: 'enabled' | 'grid' | 'pixel' | 'smart', v: boolean) =>
    useDesignUi.setState({ snap: { ...useDesignUi.getState().snap, [k]: v } });
  const box = (id: string, label: MessageKey, on: boolean, set: (v: boolean) => void, keys: string) => (
    <label title={keys} style={{ whiteSpace: 'nowrap' }}>
      <input
        type="checkbox"
        data-testid={`view-${id}`}
        checked={on}
        onChange={(e) => set(e.target.checked)}
      />{' '}
      {t(label)}
    </label>
  );
  return (
    <>
      {box('snap', 'snapOn', snap.enabled, (v) => setSnap('enabled', v), 'Ctrl+Alt+;')}
      {box('smart', 'smartGuides', snap.smart, (v) => setSnap('smart', v), 'Ctrl+U')}
      {box('snap-grid', 'snapGrid', snap.grid, (v) => setSnap('grid', v), "Ctrl+Shift+'")}
      {box('snap-pixel', 'snapPixel', snap.pixel, (v) => setSnap('pixel', v), "Ctrl+Alt+'")}
      {box('grid', 'showGrid', showGrid, (v) => useDesignUi.setState({ showGrid: v }), "Ctrl+'")}
      {box('guides', 'showGuides', showGuides, (v) => useDesignUi.setState({ showGuides: v }), 'Ctrl+;')}
    </>
  );
}

/** Dock contents arrive with their milestones; until then a dock says what it will hold. */
function DockBody({ id }: { id: DockId }) {
  const t = useT();
  const d = DOCKS.find((x) => x.id === id)!;
  const Body = d.body;
  return Body ? <Body /> : <p className={s.muted}>{t('dockEmpty')}</p>;
}

function StartScreen() {
  const t = useT();
  const lang = useLang((st) => st.lang);
  const [mine, setMine] = useState<string[] | null>(null);
  const [name, setName] = useState('');
  useEffect(() => {
    void listDesigns().then(setMine);
  }, []);
  return (
    <div className={s.start} data-testid="design-start">
      <button
        className={s.adStart}
        data-testid="start-templates"
        onClick={() => useAdUi.setState({ picker: true })}
      >
        <Megaphone className="icon" aria-hidden="true" /> {t('adTemplatesStart')}
      </button>
      <h2 style={{ marginTop: 0 }}>{t('designNewDoc')}</h2>
      <label>
        {t('designDocName')}{' '}
        <input
          type="text"
          value={name}
          placeholder={t('designUntitled')}
          onChange={(e) => setName(e.target.value)}
          data-testid="design-new-name"
        />
      </label>
      <div className={s.presets}>
        {ARTBOARD_PRESETS.map((p) => (
          <button
            key={p.id}
            data-testid={`preset-${p.id}`}
            onClick={() => void createDesign(p.id, name.trim() || t('designUntitled'))}
          >
            <strong>{lang === 'es' ? p.nameEs : p.name}</strong>
            <br />
            <span className={s.muted}>
              {p.w} × {p.h} {p.unit}
            </span>
          </button>
        ))}
      </div>
      <h3>{t('designMine')}</h3>
      {mine === null ? null : mine.length === 0 ? (
        <p className={s.muted}>{t('designNone')}</p>
      ) : (
        <ul data-testid="design-list">
          {mine.map((n) => (
            <li key={n}>
              <button onClick={() => void openDesign(n)}>{n}</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export type { MessageKey };
